import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import { Download, BarChart3, Receipt, Truck, Package, Wrench, FileSpreadsheet, TrendingDown, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";
import { toast } from "sonner";
import CustomerAcquisitionReport from "@/components/reports/CustomerAcquisitionReport";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function firstOfYear() { return `${new Date().getFullYear()}-01-01`; }
function isoDaysAgo(days) {
  const d = new Date(); d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function firstOfMonth() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function firstOfWeek() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // monday=0
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function formatEUR(n) {
  return `€${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function defaultChartFrom() {
  // Hace 11 meses, día 1
  const d = new Date();
  d.setMonth(d.getMonth() - 11, 1);
  return d.toISOString().slice(0, 7); // YYYY-MM
}
function defaultChartTo() {
  const d = new Date();
  return d.toISOString().slice(0, 7);
}

const PRESETS = [
  { id: "today",    label: "Hoy",        get: () => [todayISO(), todayISO()] },
  { id: "week",     label: "Semana",     get: () => [firstOfWeek(), todayISO()] },
  { id: "month",    label: "Mes",        get: () => [firstOfMonth(), todayISO()] },
  { id: "year",     label: "Año",        get: () => [firstOfYear(), todayISO()] },
  { id: "3m",       label: "3 meses",    get: () => [isoDaysAgo(90), todayISO()] },
  { id: "6m",       label: "6 meses",    get: () => [isoDaysAgo(180), todayISO()] },
  { id: "custom",   label: "Personalizado", get: null },
];

export default function Reports() {
  const [periodId, setPeriodId] = useState("month");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [orderBy, setOrderBy] = useState("amount");

  const [billing, setBilling] = useState({ summary: {}, invoices: [], credit_notes: [] });
  const [delivery, setDelivery] = useState({ summary: {}, delivery_notes: [] });
  const [top, setTop] = useState({ items: [] });
  const [techs, setTechs] = useState({ items: [] });
  const [expenses, setExpenses] = useState({ summary: {}, expenses: [] });
  const [monthly, setMonthly] = useState({ series: [], count: 0 });
  const [chartFrom, setChartFrom] = useState(defaultChartFrom());
  const [chartTo, setChartTo] = useState(defaultChartTo());
  const [loading, setLoading] = useState(false);

  const pickPreset = (id) => {
    setPeriodId(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p?.get) {
      const [f, t] = p.get();
      setFrom(f); setTo(t);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const qs = `date_from=${from}&date_to=${to}`;
      const chartFromIso = `${chartFrom}-01`;
      const chartToIso = `${chartTo}-28`; // suficiente para cubrir el mes
      const [b, dn, tp, tc, ex, mc] = await Promise.all([
        api.get(`/reports/billing?${qs}`),
        api.get(`/reports/delivery-notes?${qs}`),
        api.get(`/reports/top-products?${qs}&order_by=${orderBy}`),
        api.get(`/reports/repairs-by-tech?${qs}`),
        api.get(`/reports/expenses?${qs}`),
        api.get(`/reports/monthly-comparison?date_from=${chartFromIso}&date_to=${chartToIso}`),
      ]);
      setBilling(b.data);
      setDelivery(dn.data);
      setTop(tp.data);
      setTechs(tc.data);
      setExpenses(ex.data);
      setMonthly(mc.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, orderBy, chartFrom, chartTo]);

  const download = (path, filename) => {
    fetch(`${api.defaults.baseURL}${path}`, fetchAuthOpts())
      .then(async (r) => {
        if (!r.ok) { toast.error("Error al exportar"); return; }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const dateLabel = `${from}_${to}`;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1500px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 border-b border-dashed border-[#D5D7CE] pb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Admin
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight flex items-center gap-3">
            <span className="ds-ic-tile" style={{ width: 40, height: 40, borderRadius: 10 }}><BarChart3 size={20}/></span>
            Informes
          </h1>
          <p className="text-sm text-[#4B5158] mt-1">Resumen financiero y operativo del periodo seleccionado.</p>
        </div>
        <button onClick={() => download(`/reports/export/full?date_from=${from}&date_to=${to}`, `informe_completo_${dateLabel}.xlsx`)}
          data-testid="reports-export-full"
          className="ds-btn primary">
          <FileSpreadsheet size={14} /> Exportar informe completo
        </button>
      </div>

      {/* Filtros */}
      <div className="ds-card p-4">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => pickPreset(p.id)}
              data-testid={`reports-period-${p.id}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${
                periodId === p.id ? "bg-[#6E8B3D] text-white border-[#6E8B3D]" : "border-[#E5E5E0] hover:border-[#111110]"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Desde</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriodId("custom"); }}
              data-testid="reports-from"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Hasta</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriodId("custom"); }}
              data-testid="reports-to"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm" />
          </div>
        </div>
      </div>

      {loading && <div className="text-xs text-[#525252]">Cargando...</div>}

      {/* Iter 106 — Resumen económico: Facturación · Gastos · Resultado · IVA Resultado */}
      <Block title="Resumen económico" icon={TrendingUp} testid="block-economic-summary">
        {(() => {
          const facturacion = Number(billing.summary?.total_billed || 0);
          const gastos = Number(expenses.summary?.total || 0);
          const resultado = facturacion - gastos;
          const ivaRepercutido = Number(billing.summary?.total_tax || 0);
          const ivaSoportado = Number(expenses.summary?.vat_amount || 0);
          const ivaResultado = ivaRepercutido - ivaSoportado;
          const ivaLabel = ivaResultado >= 0 ? "IVA a pagar" : "IVA a compensar";
          return (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat label="Facturación" value={formatEUR(facturacion)} bold accent="green" testid="econ-facturacion" />
                <Stat label="Gastos" value={formatEUR(gastos)} bold accent="red" testid="econ-gastos" />
                <Stat
                  label="Resultado"
                  value={formatEUR(resultado)}
                  bold
                  accent={resultado >= 0 ? "green" : "red"}
                  testid="econ-resultado"
                />
                <Stat
                  label={ivaLabel}
                  value={formatEUR(Math.abs(ivaResultado))}
                  bold
                  accent={ivaResultado >= 0 ? "amber" : "green"}
                  testid="econ-iva-resultado"
                />
              </div>
              <div className="border-t border-[#E5E5E0] pt-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
                  Desglose del IVA
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Stat label="IVA repercutido (facturas)" value={formatEUR(ivaRepercutido)} testid="econ-iva-repercutido" />
                  <Stat label="IVA soportado (gastos)" value={formatEUR(ivaSoportado)} testid="econ-iva-soportado" />
                  <Stat
                    label={`Resultado IVA (${ivaResultado >= 0 ? "a ingresar" : "a compensar"})`}
                    value={formatEUR(ivaResultado)}
                    accent={ivaResultado >= 0 ? "amber" : "green"}
                  />
                </div>
                <p className="text-[11px] text-[#525252] italic mt-2">
                  Facturación e IVA repercutido computan únicamente <strong>Facturas</strong> (excluye
                  albaranes y presupuestos). IVA soportado suma el IVA registrado en Gastos.
                </p>
              </div>
            </>
          );
        })()}
      </Block>

      {/* Bloque Facturación */}
      <Block title="Facturación" icon={Receipt} testid="block-billing"
        exportBtn={<ExportBtn onClick={() => download(`/reports/export/billing?date_from=${from}&date_to=${to}`, `informe_facturas_${dateLabel}.xlsx`)} testid="reports-export-billing" />}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 mb-4">
          <Stat label="Total facturado" value={formatEUR(billing.summary?.total_billed)} bold />
          <Stat label="Base imponible" value={formatEUR(billing.summary?.total_base)} />
          <Stat label="IVA repercutido" value={formatEUR(billing.summary?.total_tax)} />
          <Stat label="Cobrado" value={formatEUR(billing.summary?.total_paid)} accent="green" />
          <Stat label="Pendiente" value={formatEUR(billing.summary?.total_pending)} accent="amber" />
          <Stat label="Nº facturas" value={billing.summary?.invoice_count || 0} />
          <Stat label="Nº abonos" value={billing.summary?.credit_count || 0} />
          <Stat label="Total abonado" value={formatEUR(billing.summary?.total_credited)} />
        </div>
        <Table headers={["Nº", "Fecha", "Cliente", "Base", "IVA", "Total", "Estado", "Pago", "Usuario", ""]}
          rows={(billing.invoices || []).map((d) => [
            d.code,
            (d.created_at || "").slice(0, 10),
            d.customer_name,
            formatEUR(d.subtotal), formatEUR(d.tax), formatEUR(d.total),
            d.status, d.payment_method, d.created_by,
            <Link to={`/billing?doc=${d.id}`} className="text-xs underline hover:text-[#6E8B3D]">Abrir →</Link>,
          ])}
          empty="Sin facturas en este periodo."
          testid="reports-invoices-table"
        />
      </Block>

      {/* Bloque Albaranes */}
      <Block title="Albaranes" icon={Truck} testid="block-delivery"
        exportBtn={<ExportBtn onClick={() => download(`/reports/export/delivery-notes?date_from=${from}&date_to=${to}`, `informe_albaranes_${dateLabel}.xlsx`)} testid="reports-export-delivery" />}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <Stat label="Nº albaranes" value={delivery.summary?.count || 0} />
          <Stat label="Total acumulado" value={formatEUR(delivery.summary?.total)} bold />
        </div>
        <Table headers={["Nº", "Fecha", "Cliente", "Total", "Estado", "Usuario", ""]}
          rows={(delivery.delivery_notes || []).map((d) => [
            d.code, (d.created_at || "").slice(0, 10), d.customer_name,
            formatEUR(d.total), d.status, d.created_by,
            <Link to={`/billing?doc=${d.id}`} className="text-xs underline hover:text-[#6E8B3D]">Abrir →</Link>,
          ])}
          empty="Sin albaranes en este periodo."
          testid="reports-delivery-table"
        />
      </Block>

      {/* Bloque Productos */}
      <Block title="Productos más facturados" icon={Package} testid="block-top-products"
        exportBtn={<ExportBtn onClick={() => download(`/reports/export/top-products?date_from=${from}&date_to=${to}&order_by=${orderBy}`, `informe_productos_${dateLabel}.xlsx`)} testid="reports-export-products" />}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-mono uppercase text-[#525252]">Ordenar por:</span>
          <button onClick={() => setOrderBy("amount")} data-testid="reports-orderby-amount"
            className={`px-2 py-1 text-xs font-mono uppercase border ${orderBy === "amount" ? "bg-[#6E8B3D] text-white border-[#6E8B3D]" : "border-[#E5E5E0]"}`}>
            Importe
          </button>
          <button onClick={() => setOrderBy("qty")} data-testid="reports-orderby-qty"
            className={`px-2 py-1 text-xs font-mono uppercase border ${orderBy === "qty" ? "bg-[#6E8B3D] text-white border-[#6E8B3D]" : "border-[#E5E5E0]"}`}>
            Cantidad
          </button>
        </div>
        <Table headers={["Ref.", "Producto / servicio", "Cantidad", "Importe", "IVA", "Veces"]}
          rows={(top.items || []).slice(0, 50).map((r) => [
            r.sku || "—", r.name, r.qty, formatEUR(r.amount), formatEUR(r.tax), r.count,
          ])}
          empty="Sin datos de productos facturados."
          testid="reports-products-table"
        />
      </Block>

      {/* Bloque Técnicos */}
      <Block title="Reparaciones por técnico" icon={Wrench} testid="block-tech"
        exportBtn={<ExportBtn onClick={() => download(`/reports/export/repairs-by-tech?date_from=${from}&date_to=${to}`, `informe_reparaciones_tecnico_${dateLabel}.xlsx`)} testid="reports-export-tech" />}>
        <Table headers={["Técnico", "Total", "Abiertas", "Finalizadas", "Pendientes", "RMA"]}
          rows={(techs.items || []).map((r) => [
            r.technician, r.total, r.open, r.closed, r.pending, r.rma,
          ])}
          empty="Sin reparaciones en este periodo."
          testid="reports-tech-table"
        />
      </Block>

      {/* Bloque Gastos */}
      <Block title="Gastos" icon={TrendingDown} testid="block-expenses">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Total gastos" value={formatEUR(expenses.summary?.total)} bold accent="red" />
          <Stat label="IVA soportado" value={formatEUR(expenses.summary?.vat_amount)} />
          <Stat label="Nº gastos" value={expenses.summary?.count || 0} />
          <Stat label="Auto-generados" value={expenses.summary?.auto_generated || 0} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Stat label="Facturado" value={formatEUR(expenses.summary?.total_billed)} accent="green" />
          <Stat label="Gastos" value={formatEUR(expenses.summary?.total)} accent="red" />
          <Stat label="Diferencia" value={formatEUR(expenses.summary?.difference)}
                accent={(expenses.summary?.difference || 0) >= 0 ? "green" : "amber"}
                bold />
        </div>

        {/* Iter 122 — Informe de proveedores + top spender */}
        <SupplierReportBlock from={chartFrom} to={chartTo} />

        {/* Gráfica mensual: Facturado vs Gastos rango personalizado */}
        <div className="mt-6 border border-[#E5E5E0] p-3 bg-white" data-testid="reports-monthly-chart">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              Facturado vs Gastos · rango personalizado ({monthly.count || 0} meses)
            </div>
            <div className="flex items-center gap-2">
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Desde</label>
              <input
                type="month"
                value={chartFrom}
                onChange={(e) => setChartFrom(e.target.value)}
                data-testid="reports-chart-from"
                className="ds-card focus:border-[#6E8B3D] outline-none px-2 py-1 text-xs font-mono"
              />
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Hasta</label>
              <input
                type="month"
                value={chartTo}
                onChange={(e) => setChartTo(e.target.value)}
                data-testid="reports-chart-to"
                className="ds-card focus:border-[#6E8B3D] outline-none px-2 py-1 text-xs font-mono"
              />
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart
                data={monthly.series}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fontFamily: "monospace", fill: "#525252" }}
                />
                <YAxis
                  tick={{ fontSize: 10, fontFamily: "monospace", fill: "#525252" }}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`}
                />
                <Tooltip
                  formatter={(v) => formatEUR(v)}
                  labelStyle={{ fontFamily: "monospace", fontSize: 11 }}
                  contentStyle={{ background: "#ffffff", border: "1px solid #111110", borderRadius: 0, fontSize: 11 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                <Bar dataKey="billed" name="Facturado" fill="#6E8B3D" />
                <Bar dataKey="expenses" name="Gastos" fill="#B0413E" />
                <Bar dataKey="difference" name="Diferencia">
                  {monthly.series.map((d) => (
                    <Cell key={d.label || d.month} fill={d.difference >= 0 ? "#3F6B1C" : "#D97706"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <p className="text-[10px] font-mono uppercase tracking-widest text-[#525252] mt-2">
          // comparativa orientativa, no es contabilidad oficial
        </p>
      </Block>

      <CustomerAcquisitionReport from={from} to={to} />
    </div>
  );
}

function Block({ title, icon: Icon, children, testid, exportBtn }) {
  return (
    <section className="ds-card" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
        <div className="font-display font-bold text-base flex items-center gap-2">
          <Icon size={16} /> {title}
        </div>
        {exportBtn}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Stat({ label, value, bold, accent }) {
  const colorClass = accent === "green" ? "text-[#6E8B3D]" :
                    accent === "amber" ? "text-amber-700" :
                    accent === "red" ? "text-red-700" : "text-[#111110]";
  return (
    <div className="border border-[#E5E5E0] p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className={`mt-1 font-mono ${bold ? "text-xl font-bold" : "text-lg"} ${colorClass}`}>{value}</div>
    </div>
  );
}

function Table({ headers, rows, empty, testid }) {
  return (
    <div className="border border-[#E5E5E0] overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-[#F7F7F4] border-b border-[#E5E5E0] font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-3 py-4 text-center text-xs text-[#525252]">{empty}</td></tr>
          ) : rows.map((row, idx) => {
            // Generamos una key estable concatenando los primeros 2 valores
            // de la fila (suelen ser identificadores como código + descripción).
            const rowKey = `row-${idx}-${(row[0] ?? "")}-${(row[1] ?? "")}`;
            return (
              <tr key={rowKey} className="border-b border-[#E5E5E0] last:border-0">
                {row.map((cell, ci) => (
                  <td key={`cell-${rowKey}-${ci}`} className="px-3 py-2 text-xs">{cell}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExportBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 border border-[#111110] hover:bg-[#111110] hover:text-white px-2.5 py-1 text-xs font-mono uppercase tracking-wider">
      <Download size={12} /> Excel
    </button>
  );
}

// Iter 122 — Bloque de informe por proveedor + top spender + export xlsx.
function SupplierReportBlock({ from, to }) {
  const [data, setData] = useState({ rows: [], top_spender: null, totals: {} });
  const [orderBy, setOrderBy] = useState("total_desc");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  // Convertir "YYYY-MM" a "YYYY-MM-01" y último día del mes.
  const df = from ? `${from}-01` : "";
  const dt = to ? `${to}-31` : "";
  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ order_by: orderBy });
      if (df) qs.set("date_from", df);
      if (dt) qs.set("date_to", dt);
      const r = await api.get(`/reports/suppliers?${qs.toString()}`);
      setData(r.data || { rows: [], top_spender: null, totals: {} });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderBy, from, to]);
  const openDetail = async (sid) => {
    setSelected(sid);
    try {
      const r = await api.get(`/suppliers/${sid}/expenses`);
      setDetail(r.data);
    } catch (e) { setDetail(null); }
  };
  const download = async () => {
    const qs = new URLSearchParams({ order_by: orderBy });
    if (df) qs.set("date_from", df);
    if (dt) qs.set("date_to", dt);
    try {
      const r = await api.get(`/reports/suppliers/export?${qs.toString()}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `proveedores_${df || "all"}_${dt || "all"}.xlsx`; a.click();
    } catch (e) { console.error(e); }
  };
  return (
    <div className="mt-6 border border-[#E5E5E0] p-3 bg-white" data-testid="reports-suppliers-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
          Gasto por proveedor · {data.rows.length} proveedor{data.rows.length === 1 ? "" : "es"}
        </div>
        <div className="flex items-center gap-2">
          <select value={orderBy} onChange={(e) => setOrderBy(e.target.value)}
            data-testid="suppliers-order-by"
            className="ds-card px-2 py-1 text-xs">
            <option value="total_desc">Mayor gasto</option>
            <option value="total_asc">Menor gasto</option>
            <option value="name">Nombre</option>
            <option value="count">Nº operaciones</option>
          </select>
          <button onClick={download} data-testid="suppliers-export"
            className="inline-flex items-center gap-1 border border-[#111110] hover:bg-[#111110] hover:text-white px-2 py-1 text-xs font-mono uppercase">
            Excel
          </button>
        </div>
      </div>
      {data.top_spender && (
        <div className="border-2 border-[#111110] bg-[#F7F7F4] p-3 mb-3" data-testid="suppliers-top">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252]">Proveedor con mayor gasto</div>
          <div className="text-lg font-black">{data.top_spender.supplier_name}</div>
          <div className="text-xs">Total: <b>{formatEUR(data.top_spender.total)}</b> · {data.top_spender.count} operaciones</div>
        </div>
      )}
      <div className="overflow-x-auto max-h-[420px]">
        <table className="w-full text-xs">
          <thead className="bg-[#F7F7F4] sticky top-0">
            <tr>
              <th className="text-left px-2 py-1 font-mono uppercase tracking-widest text-[10px]">Proveedor</th>
              <th className="text-right px-2 py-1 font-mono uppercase tracking-widest text-[10px]">Nº</th>
              <th className="text-right px-2 py-1 font-mono uppercase tracking-widest text-[10px]">Base</th>
              <th className="text-right px-2 py-1 font-mono uppercase tracking-widest text-[10px]">IVA</th>
              <th className="text-right px-2 py-1 font-mono uppercase tracking-widest text-[10px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-3 text-center text-[#525252]">Cargando...</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={5} className="p-3 text-center text-[#525252]">Sin gastos en el periodo.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.supplier_id} className="border-t border-[#E5E5E0] hover:bg-[#F0F4E8] cursor-pointer"
                onClick={() => openDetail(r.supplier_id)}
                data-testid={`supplier-row-${r.supplier_id}`}>
                <td className="px-2 py-1">{r.supplier_name} {r.supplier_tax_id && <span className="text-[#525252] font-mono text-[10px]">· {r.supplier_tax_id}</span>}</td>
                <td className="px-2 py-1 text-right">{r.count}</td>
                <td className="px-2 py-1 text-right">{formatEUR(r.base)}</td>
                <td className="px-2 py-1 text-right">{formatEUR(r.vat)}</td>
                <td className="px-2 py-1 text-right font-semibold">{formatEUR(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && detail && (
        <div className="mt-3 border border-[#111110] bg-[#F7F7F4] p-3" data-testid="supplier-detail">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest">Detalle de gastos — Total: {formatEUR(detail.total)} · IVA: {formatEUR(detail.vat)}</div>
            <button onClick={() => { setSelected(null); setDetail(null); }} className="text-xs text-[#525252] hover:underline">Cerrar</button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left px-2 py-0.5 font-mono uppercase tracking-widest text-[10px]">Fecha</th>
                  <th className="text-left px-2 py-0.5 font-mono uppercase tracking-widest text-[10px]">Concepto</th>
                  <th className="text-right px-2 py-0.5 font-mono uppercase tracking-widest text-[10px]">Base</th>
                  <th className="text-right px-2 py-0.5 font-mono uppercase tracking-widest text-[10px]">IVA</th>
                  <th className="text-right px-2 py-0.5 font-mono uppercase tracking-widest text-[10px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {detail.expenses.map((e) => (
                  <tr key={e.id} className="border-t border-[#E5E5E0]">
                    <td className="px-2 py-0.5">{e.expense_date}</td>
                    <td className="px-2 py-0.5">{e.description}</td>
                    <td className="px-2 py-0.5 text-right">{formatEUR(e.base_amount)}</td>
                    <td className="px-2 py-0.5 text-right">{formatEUR(e.vat_amount)}</td>
                    <td className="px-2 py-0.5 text-right font-semibold">{formatEUR(e.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
